import React from "react";

import {
  getAuthorData,
  getAuthorPhoto
} from "../../../../../lib/analysisHelper";
import { appendStockFilterToAnalysisListLinks } from "../../../../../lib/appendStockFilterToAnalysisListLinks";
import PageTitle from "../../../common/PageTitle";
import ClickableContent from "../../../common/ClickableContent";
import moment from "moment-mini";
import classNames from "classnames";
import LandingTypography from "../../../landingPage/pages/Homepage/components/LandingTypography";
import { StockBasicDataSummaryWrapper } from "../../../common/StockBasicDataSummary";
import withTranslations from "../../../../../hocs/withTranslations";
import { compose } from "redux";
import useIsDesktop from "../../../landingPage/hooks/useIsDesktop";
import AnalysisFormation, {
  ANALYSIS_FORMATION_VARIANTS
} from "./AnalysisFormation/AnalysisFormation";
import AnalysisHighlights from "./AnalysisHighlights";

function AnalysisContentFactory({
  translate,
  locale,
  response,
  analysisCategory,
  // Stock-page embed: omit page title + quote card (already on the stock page).
  compact = false
}) {
  const isDesktop = useIsDesktop();
  const {
    stock,
    content,
    publication_date,
    category,
    highlights,
    trend
  } = response;

  const createdMoment = moment(publication_date);
  const categoryDescriptionHtml = appendStockFilterToAnalysisListLinks(
    analysisCategory?.description,
    {
      ticker: stock?.ticker,
      market: stock?.market,
      stockName: stock?.name || stock?.short_name,
      categoryId: analysisCategory?.value ?? analysisCategory?.id
    }
  );

  const title = translate(`analysis-content-title_${category}`, {
    stockName: stock.short_name
  });
  const authorPhoto = getAuthorPhoto(response);

  return (
    <>
      <LandingTypography>
        <header>
          <div
            className={classNames("flex justify-between", {
              "flex-row gap-8": isDesktop,
              "flex-col gap-5": !isDesktop
            })}
          >
            <div>
              {compact ? null : (
                <h1
                  className={classNames(
                    "font-bold text-black mt-0 normal-case",
                    {
                      "text-2xl mb-4": isDesktop,
                      "text-xl mb-5": !isDesktop
                    }
                  )}
                >
                  {title}
                </h1>
              )}
              <div className="flex flex-row gap-3 items-center flex-wrap">
                <div className="flex items-center gap-2">
                  <span
                    className={classNames("text-black-8", {
                      "text-sm": isDesktop,
                      "text-xs": !isDesktop
                    })}
                  >
                    {translate("Status")}:
                  </span>
                  <strong
                    className={classNames("font-bold", {
                      "text-green": response.active,
                      "text-red": !response.active,
                      "text-sm": isDesktop,
                      "text-xs": !isDesktop
                    })}
                  >
                    {response.active
                      ? translate("Active")
                      : translate("Inactive")}
                  </strong>
                </div>
                <div className={"hr-vertical-1 hr-vertical_16"} />
                <div className="flex items-center gap-2">
                  <span className="text-sm text-black-8">
                    {translate("Author")}:
                  </span>
                  <div className="flex items-center gap-1.5">
                    {authorPhoto ? (
                      <img
                        className={"shrink-0 rounded-full object-cover"}
                        src={authorPhoto}
                        alt=""
                        width={16}
                        height={16}
                      />
                    ) : null}
                    <span
                      className={classNames("text-black font-medium", {
                        "text-sm": isDesktop,
                        "text-xs": !isDesktop
                      })}
                    >
                      {getAuthorData(response)}
                    </span>
                  </div>
                </div>
                <div className={"hr-vertical-1 hr-vertical_16"} />
                <div className="flex items-center gap-2">
                  <span
                    className={classNames("text-black-8", {
                      "text-sm": isDesktop,
                      "text-xs": !isDesktop
                    })}
                  >
                    {translate("analysis-content-factory_created-at")}:
                  </span>
                  <time
                    dateTime={createdMoment.toISOString()}
                    className={classNames("text-black font-medium", {
                      "text-sm": isDesktop,
                      "text-xs": !isDesktop
                    })}
                  >
                    {createdMoment.format("DD.MM.YYYY, HH:mm")}
                  </time>
                </div>
              </div>
            </div>
            {compact ? null : <StockBasicDataSummaryWrapper stock={stock} />}
          </div>
          <hr className="hr-horizontal-2 my-5" />
        </header>
        <section>
          {highlights?.title || highlights?.subtitle ? (
            <div
              className={classNames({
                "mb-5": !isDesktop,
                "mb-6": isDesktop
              })}
            >
              <AnalysisFormation
                variant={
                  trend === "up"
                    ? ANALYSIS_FORMATION_VARIANTS.HAMMER_UP
                    : trend === "down"
                    ? ANALYSIS_FORMATION_VARIANTS.HAMMER_DOWN
                    : null
                }
                title={highlights?.title}
                subtitle={highlights?.subtitle}
              />
            </div>
          ) : null}
          {highlights ? (
            <AnalysisHighlights
              stockId={stock.id}
              stockName={stock.name ?? stock.short_name}
              locale={locale}
              currency={stock.currency}
              highlights={highlights}
              title={title}
            />
          ) : null}
        </section>
      </LandingTypography>
      {compact ? null : <PageTitle title={title} />}
      <ClickableContent dangerouslySetInnerHTML={{ __html: content }} />
      {categoryDescriptionHtml ? (
        <small
          className="category-description"
          dangerouslySetInnerHTML={{
            __html: categoryDescriptionHtml
          }}
        />
      ) : null}
    </>
  );
}

export default compose(withTranslations)(AnalysisContentFactory);
